import React from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
    buildOAuthCallbackUrl,
    buildRegisterRedirectPath,
    resolveAuthorizedPostAuthRedirectTarget,
    resolveDefaultPostLoginPath,
    resolveLoginRedirectTarget
} from '../components/routing/loginRedirect';
import { useGetUserLeagues } from '../api/leaguesApi';
import { useAuth } from '../hooks/useAuth';
import { REMEMBER_ME_STORAGE_KEY, setRememberMePreference, supabase } from '../lib/supabaseClient';
import { clearClientAuthState } from '../utils/authCleanup';
import posthog from 'posthog-js';
import { SocialAuthButtons } from '../components/auth/SocialAuthButtons';

function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [rememberMe, setRememberMe] = useState(() => {
        try {
            return localStorage.getItem(REMEMBER_ME_STORAGE_KEY) !== 'false';
        } catch (_) {
            return true;
        }
    });
    const [pendingDefaultNavigation, setPendingDefaultNavigation] = useState(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectParam = searchParams.get('redirect');
    const explicitRedirectTo = resolveLoginRedirectTarget(redirectParam);
    const registerPath = buildRegisterRedirectPath(redirectParam);
    const { login, loginWithOAuth, loading: authLoading, user } = useAuth();
    const { data: userLeagues, isLoading: leaguesLoading } = useGetUserLeagues(!!user);
    const waitingForDefaultDestination = (
        pendingDefaultNavigation &&
        (authLoading || (!!user && leaguesLoading))
    );
    const loginDisabled = authLoading || waitingForDefaultDestination;

    useEffect(() => {
        if (pendingDefaultNavigation && !authLoading && !user) {
            setPendingDefaultNavigation(false);
            setError('Login succeeded, but Nova could not load your profile. Please try again.');
            return;
        }

        if (authLoading || !user || leaguesLoading) {
            return;
        }

        const authorizedRedirect = resolveAuthorizedPostAuthRedirectTarget(explicitRedirectTo, userLeagues, user);
        if (authorizedRedirect) {
            navigate(authorizedRedirect, { replace: true });
            return;
        }

        navigate(resolveDefaultPostLoginPath(userLeagues, user), { replace: true });
    }, [
        user,
        authLoading,
        explicitRedirectTo,
        pendingDefaultNavigation,
        leaguesLoading,
        userLeagues,
        navigate
    ]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        setError('');
        setPendingDefaultNavigation(false);
        try {
            setRememberMePreference(rememberMe);
            const { error: loginError } = await login({ email, password });
            
            if (loginError) {
                throw loginError;
            }

            console.log('Login successful via context');
            posthog.capture('user_logged_in');
            setPendingDefaultNavigation(true);

        } catch (err) {
            console.error('Login failed:', err);
            posthog.captureException(err, { event: 'login_failed' });
            setPendingDefaultNavigation(false);
            const backendError = err?.message || 'Login failed. Please check credentials.';
            setError(backendError);
        }
    };

    const handleOAuthSignIn = async (provider, providerName) => {
        setError('');
        setPendingDefaultNavigation(false);

        try {
            setRememberMePreference(rememberMe);
            await loginWithOAuth(provider, {
                redirectTo: buildOAuthCallbackUrl(explicitRedirectTo)
            });
        } catch (err) {
            console.error(`${providerName} login failed:`, err);
            setError(err?.message || `${providerName} sign-in failed. Please try again.`);
        }
    };

    const handleGoogleSignIn = () => handleOAuthSignIn('google', 'Google');

    const handleXSignIn = () => handleOAuthSignIn('x', 'X');

    return (
        <div className="page-login">
            <div className="wrapper">
                <h1>LOG IN</h1>


                <div className="form-block">
                    <form onSubmit={(e) => e.preventDefault()}>
                        {error && (
                            <div className="error-message" style={{ color: 'red', marginBottom: '1rem' }}>
                                {error}
                            </div>
                        )}
                        
                        <div className="form-group">
                            <label htmlFor="i1">E-MAIL</label>
                            <input 
                                name="email" 
                                id="i1" 
                                type="email"
                                placeholder="Email address"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                autoComplete="username"
                                required
                            />
                        </div>
                        
                        <div className="form-group">
                            <label htmlFor="i2">PASSWORD</label>
                            <input 
                                name="password"
                                id="i2" 
                                type="password"
                                placeholder="Password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                autoComplete="current-password"
                                required
                            />
                            <div className="tip">
                                <Link to="/password-reset-request">Forgot password?</Link>
                            </div>
                        </div>

                        <div className="checkbox-block" style={{ marginTop: '-0.5rem' }}>
                            <input
                                id="remember-me"
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <label htmlFor="remember-me">REMEMBER ME</label>
                        </div>

                        <button 
                            type="button" 
                            className="submit-btn"
                            disabled={loginDisabled}
                            onClick={handleSubmit}
                        >
                            {loginDisabled ? 'Logging In...' : 'LOG IN'}
                        </button>

                        <SocialAuthButtons
                            disabled={loginDisabled}
                            onGoogle={handleGoogleSignIn}
                            onX={handleXSignIn}
                        />
                    </form>

                    <h4>
                        DON'T HAVE AN ACCOUNT?
                    </h4>

                    <Link to={registerPath} className="submit-btn">
                        REGISTER
                    </Link>
                    <div style={{ marginTop: '1rem', textAlign: 'center' }}>
                        <button
                            type="button"
                            className="submit-btn"
                            onClick={async () => {
                                void fetch('/api/auth/logout', {
                                    method: 'POST',
                                    credentials: 'include'
                                }).catch(() => {});
                                try { await supabase.auth.signOut(); } catch (_) {
                                    // Continue with local cleanup even if Supabase sign-out fails.
                                }
                                clearClientAuthState();
                                window.location.reload();
                            }}
                        >
                            Reset Session
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { LoginPage };
